import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo } from "react";
import { useParams } from "react-router";

import { getAvailableSlots } from "../../api/available-slots/get-available-slots";
import { FIRST_DAY_OF_WEEK } from "../../components/calendar/calendar.constants";
import { getWeeksOfMonth } from "../../components/calendar/calendar.helpers";
import { useGeneralStore } from "../../store/general/general-store";

/**
 * Custom hook to fetch available time slots for a given date range.
 *
 * @param dateToDisplay - The date to display in the calendar view
 * @returns React Query result with available time slots data
 */
export function useGetAvailableTimeSlots(dateToDisplay: Date) {
  const queryKey = "availableSlots";
  const { urlSlug } = useParams();
  const { currentTimezone } = useGeneralStore();

  const { firstDay, lastDay } = useMemo(() => {
    if (!dateToDisplay) {
      return { firstDay: null, lastDay: null };
    }

    const weeks = getWeeksOfMonth(FIRST_DAY_OF_WEEK, dateToDisplay);
    const firstWeek = weeks[0];
    const lastWeek = weeks[weeks.length - 1];

    if (!firstWeek || !lastWeek) {
      return { firstDay: null, lastDay: null };
    }

    return {
      firstDay: format(firstWeek.week[0].day, "yyyy-MM-dd"),
      lastDay: format(lastWeek.week[lastWeek.week.length - 1].day, "yyyy-MM-dd"),
    };
  }, [dateToDisplay]);

  return useQuery({
    queryKey: [queryKey, urlSlug, firstDay, lastDay, currentTimezone],
    queryFn: async () => {
      if (!urlSlug || !firstDay || !lastDay || !currentTimezone) {
        throw new Error("Missing required parameters for fetching available slots");
      }

      return getAvailableSlots({
        urlSlug,
        dates: {
          from: firstDay,
          to: lastDay,
        },
        timezone: currentTimezone,
      });
    },
    enabled: Boolean(urlSlug && firstDay && lastDay && currentTimezone),
    staleTime: 0, // Always refetch when query key changes
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
